'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { Icon, useToast } from '@/src/components/ui';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmptyTileWrapper } from '../../profile/components';
import { compressImage } from '../../bits/components/ImageUpload';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@workspace/ui/components/dialog';

export type ImageItem = {
  clientId: string;
  file?: File;
  url: string;
  order?: number;
};

type Props = {
  screenshots?: string[];
  value?: ImageItem[];
  onChange?: (items: ImageItem[]) => void;
  className?: string;
  readonly?: boolean;
};

const useMounted = () => {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
};

const reorderWithOrder = (arr: ImageItem[]) =>
  arr.map((t, idx) => ({ ...t, order: idx }));

function SortableBlock({
  item,
  onRemove,
  readonly,
}: {
  item: ImageItem;
  onRemove: (clientId: string) => void;
  readonly?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.clientId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    touchAction: 'none',
  };

  return (
    <div
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative border rounded-lg border-gray-200 bg-transparent w-[140px] h-[140px] md:w-[160px] md:h-[160px] shrink-0',
        { 'cursor-grab active:cursor-grabbing': !readonly }
      )}
    >
      <img
        src={item.url}
        alt="Screenshot"
        className="w-full h-full object-cover rounded-lg"
        draggable={false}
      />
      {!readonly && (
        <>
          <button
            type="button"
            {...attributes}
            {...listeners}
            className={cn(
              'absolute right-1/2 -bottom-3 translate-x-1/2 rounded-full bg-black/90 text-white p-2 shadow',
              'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
              'transition-opacity duration-150'
            )}
            aria-label="Drag to reorder"
            title="Drag to reorder"
          >
            <Icon name="grip" className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(item.clientId)}
            className={cn(
              'absolute -top-3 -right-3 rounded-full bg-black/90 text-white p-2 shadow',
              'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
              'transition-opacity duration-150'
            )}
            aria-label="Remove"
            title="Remove"
          >
            <Icon name="delete-tile" className="size-4" />
          </button>
        </>
      )}
    </div>
  );
}

function PreviewThumb({ url, onClick }: { url: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative border rounded-lg border-gray-200 bg-transparent w-[140px] h-[140px] md:w-[160px] md:h-[160px] shrink-0 overflow-hidden hover:cursor-pointer"
      aria-label="Open image"
      title="Open image"
    >
      <img
        src={url}
        alt="Screenshot"
        className="w-full h-full object-cover rounded-lg pointer-events-none"
        draggable={false}
      />
    </button>
  );
}

export const Screenshots = ({
  value,
  onChange,
  screenshots,
  readonly = false,
  className,
}: Props) => {
  const mounted = useMounted();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const urlsToRevokeRef = useRef<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const items = useMemo(() => {
    const v = value ?? [];
    return [...v].sort((a, b) => {
      const ao = a.order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.order ?? Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }, [value]);

  const previewUrls = useMemo(() => {
    if ((value?.length ?? 0) > 0) return items.map((i) => i.url);
    return screenshots ?? [];
  }, [items, screenshots, value]);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 0);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [mounted, value]);

  useEffect(() => {
    return () => {
      urlsToRevokeRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsToRevokeRef.current.clear();
    };
  }, []);

  const scrollByStep = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(300, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
  };

  const handleFilePickClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;

    const nextItems: ImageItem[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}": file size must be less than 10MB`);
        continue;
      }

      let prepared: File;
      try {
        const maybe = await compressImage(file);
        prepared =
          maybe instanceof File
            ? maybe
            : new File([maybe], file.name, { type: file.type || 'image/png' });
      } catch {
        prepared = file;
      }

      const url = URL.createObjectURL(prepared);
      urlsToRevokeRef.current.add(url);

      nextItems.push({
        clientId:
          globalThis.crypto?.randomUUID?.() ??
          `img_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        url,
        file: prepared,
      });
    }

    if (nextItems.length) {
      onChange?.(reorderWithOrder([...items, ...nextItems]));
      requestAnimationFrame(() => scrollByStep('right'));
    }
  };

  const handleRemove = (clientId: string) => {
    const item = items.find((t) => t.clientId === clientId);
    if (item?.url && urlsToRevokeRef.current.has(item.url)) {
      URL.revokeObjectURL(item.url);
      urlsToRevokeRef.current.delete(item.url);
    }
    const next = items.filter((t) => t.clientId !== clientId);
    onChange?.(reorderWithOrder(next));
    requestAnimationFrame(updateArrows);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.clientId === active.id);
    const newIndex = items.findIndex((i) => i.clientId === over.id);
    const moved = arrayMove(items, oldIndex, newIndex);
    onChange?.(reorderWithOrder(moved));
    requestAnimationFrame(updateArrows);
  };

  const openPreviewAt = (idx: number) => {
    if (!readonly) return;
    setCurrentIdx(idx);
    setOpen(true);
  };
  const showPrev = () => {
    if (!screenshots?.length) return;
    setCurrentIdx(
      (prev) => (prev - 1 + screenshots.length) % screenshots.length
    );
  };
  const showNext = () => {
    if (!screenshots?.length) return;
    setCurrentIdx((prev) => (prev + 1) % screenshots.length);
  };

    if (readonly && (!value || value.length === 0) && (!screenshots || screenshots.length === 0)) {
      return null;
    }

  return (
    <div
      className={cn(
        'w-full',
        {
          'md:mt-6': readonly,
        },
        className
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="md:text-2xl! text-lg! font-semibold! md:mt-1 mt-5">
          Screenshots
        </h3>

        {(items.length > 5 || (screenshots?.length ?? 0) > 5) && (
          <div className="flex items-center gap-2 md:mt-1">
            <button
              type="button"
              onClick={() => scrollByStep('left')}
              disabled={!canLeft}
              className={cn(
                'inline-flex items-center justify-center size-9 rounded-full bg-white shadow border',
                'hover:bg-blue-primary active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              aria-label="Scroll left"
              title="Scroll left"
            >
              <Icon name="left" className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => scrollByStep('right')}
              disabled={!canRight}
              className={cn(
                'inline-flex items-center justify-center size-9 rounded-full bg-white shadow border',
                'hover:bg-blue-primary active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              aria-label="Scroll right"
              title="Scroll right"
            >
              <Icon name="right" className="size-3" />
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        {mounted ? (
          previewUrls && readonly ? (
            <>
              <div
                ref={scrollRef}
                className="md:h-[190px] flex gap-3 items-center overflow-x-auto hide-scrollbar"
              >
                {previewUrls.map((url, idx) => (
                  <PreviewThumb
                    key={`${url}-${idx}`}
                    url={url}
                    onClick={() => openPreviewAt(idx)}
                  />
                ))}
              </div>

              <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-5xl w-full flex flex-col items-center p-3">
                  <DialogTitle></DialogTitle>
                  <div className="relative w-full flex items-center justify-center">
                    {previewUrls.length > 0 && (
                      <img
                        src={previewUrls[currentIdx]}
                        alt=""
                        className="max-h-[80vh] object-contain rounded"
                      />
                    )}

                    {previewUrls.length > 1 && (
                      <>
                        <button
                          onClick={showPrev}
                          aria-label="Previous"
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-2 hover:cursor-pointer"
                        >
                          <Icon
                            name="chevron-left"
                            className="w-5 h-5 text-blue-primary"
                          />
                        </button>
                        <button
                          onClick={showNext}
                          aria-label="Next"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-2 hover:cursor-pointer"
                        >
                          <Icon
                            name="chevron-left"
                            className="rotate-180 w-5 h-5 text-blue-primary"
                          />
                        </button>
                      </>
                    )}
                  </div>

                  {previewUrls.length > 1 && (
                    <div className="mt-2 text-sm text-gray-500">
                      {currentIdx + 1} / {previewUrls.length}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={items.map((i) => i.clientId)}
                strategy={horizontalListSortingStrategy}
              >
                <div
                  ref={scrollRef}
                  className="h-[190px] flex gap-3 items-center overflow-x-auto hide-scrollbar"
                >
                  {items.map((item) => (
                    <SortableBlock
                      key={item.clientId}
                      item={item}
                      onRemove={handleRemove}
                      readonly={readonly}
                    />
                  ))}

                  {!readonly ? (
                    <div className="md:w-[160px] md:h-[160px] w-[160px] h-[160px]">
                      <EmptyTileWrapper
                        isMyProfile
                        onClick={handleFilePickClick}
                      >
                        <div className="flex flex-col items-center h-full w-full justify-center gap-3 min-w-[160px]">
                          <div className="flex items-center justify-center size-9 border bg-bg-gray rounded-sm">
                            <Icon
                              name="upload"
                              width={13}
                              height={17}
                              className="text-black/40"
                            />
                          </div>
                          <span className="text-sm font-medium text-black/40">
                            Upload images
                          </span>
                          <span className="text-xs text-black/40 font-light">
                            up to 10MB each
                          </span>
                        </div>
                      </EmptyTileWrapper>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>
                  ) : null}
                </div>
              </SortableContext>
            </DndContext>
          )
        ) : (
          <div className="flex gap-2 overflow-x-auto py-5">
            {items.map((i) => (
              <div
                key={i.clientId}
                className="w-[160px] h-[160px] rounded-lg bg-gray-100 animate-pulse"
              />
            ))}
            <div className="w-[160px] h-[160px] rounded-lg border-gray-300" />
          </div>
        )}
      </div>
    </div>
  );
};
